/**
 * 文件定位：
 * 1. 当前文件位于 web-client/views/editor；
 * 2. 负责装配设计树 Feature 删除动作的 UI 确认、Viewer 执行和模型历史提交；
 * 3. Editor.vue 只注入依赖，不承载删除领域流程，避免继续膨胀宿主组件。
 */

import {
    captureFeatureHistorySnapshotFromSummary,
    createChangeSet,
    createEmptyDirtyScope,
    createEmptySnapshotScope,
    createExistingHistorySnapshot,
    createFeatureDefinitionHistoryPatch,
    createMissingHistorySnapshot,
    createSketchGroupRuntimeHistoryPatch,
    FEATURE_DELETION_PLAN_CHANGED_MESSAGE,
    resolveEntityPatchOperation,
    SketchDisplayState,
} from '@puppycad/cad-core';
import type {
    CadTransaction,
    FeatureDeletionExecutionResult,
    FeatureDeletionPlan,
    FeatureRollbackHistorySnapshot,
    HistoryEntitySnapshot,
    SketchGroupRuntimeHistorySnapshot,
    Viewer,
} from '@puppycad/cad-core';
import type {FeatureTreeFeatureDeleteActionInput} from '@/hooks/feature-tree/feature-tree-types';
import {
    requestFeatureTreeDeleteConfirmation,
    type FeatureTreeDeleteConfirmItem,
} from '@/views/editor/requestFeatureTreeDeleteConfirmation';

/**
 * 特征树 Feature 删除动作处理器依赖。
 */
export interface FeatureTreeFeatureDeleteActionHandlerDependencies {
    /** 读取当前 Viewer。 */
    readonly getViewer: () => Viewer | null;
    /** NaiveUI dialog 能力。 */
    readonly dialog: {
        /** 创建对话框。 */
        create: (options: Record<string, unknown>) => unknown;
    };
    /** NaiveUI message 能力。 */
    readonly message: {
        /** 警告提示。 */
        warning: (content: string) => void;
    };
    /** 串行运行模型历史动作。 */
    readonly runExclusive: <T>(work: () => Promise<T>) => Promise<T>;
    /** 分配模型历史序号。 */
    readonly allocateHistorySequence: () => number;
    /** 提交模型历史事务。 */
    readonly commitTransaction: (transaction: CadTransaction) => Promise<CadTransaction | null>;
    /** 读取当前文档 ID。 */
    readonly getDocumentId: () => string;
    /** 统一上报异步失败。 */
    readonly reportFailure: (error: unknown, fallbackMessage: string) => void;
    /** 标记 Feature 定义快照已经被删除。 */
    readonly markFeatureDefinitionsDeleted: (featureIds: readonly string[]) => void;
}

/**
 * 从删除计划中读取特征摘要。
 *
 * @param plan 特征删除计划。
 * @param featureId 特征 ID。
 * @returns 特征摘要；未命中时返回 `null`。
 */
function resolveFeatureDeletionSummary(
    plan: FeatureDeletionPlan,
    featureId: string,
): FeatureDeletionPlan['targetFeature'] | null {
    if (plan.targetFeature.featureId === featureId) {
        return plan.targetFeature;
    }

    return plan.childFeatures.find((feature) => feature.featureId === featureId) ?? null;
}

/**
 * 创建特征删除确认框中的相关项目列表。
 *
 * @param plan 特征删除计划。
 * @returns 下游特征与其来源草图组成的相关项目列表。
 */
function createFeatureDeletionRelatedItems(plan: FeatureDeletionPlan): FeatureTreeDeleteConfirmItem[] {
    const relatedItems: FeatureTreeDeleteConfirmItem[] = [];
    const pushedIds = new Set<string>();
    const deletedSketchIdSet = new Set(plan.deletedSketchIds);
    const pushItem = (item: FeatureTreeDeleteConfirmItem): void => {
        if (pushedIds.has(item.id)) {
            return;
        }
        pushedIds.add(item.id);
        relatedItems.push(item);
    };

    plan.childFeatures.forEach((feature) => {
        pushItem({
            id: feature.featureId,
            label: feature.displayName,
            kind: 'feature',
        });
    });

    plan.absorbedSketches
        .filter((sketch) => deletedSketchIdSet.has(sketch.sketchId))
        .forEach((sketch) => {
            pushItem({
                id: sketch.sketchId,
                label: sketch.displayName,
                kind: 'sketch',
            });
        });

    return relatedItems;
}

/**
 * 判断值是否为普通对象记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通对象记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * 捕获删除计划涉及草图的显示态。
 *
 * @param plan 特征删除计划。
 * @param currentViewer 当前 Viewer。
 * @returns 草图显示态映射。
 */
function captureFeatureDeletionSketchDisplayStates(
    plan: FeatureDeletionPlan,
    currentViewer: Viewer,
): Map<string, SketchDisplayState | null> {
    const displayStates = new Map<string, SketchDisplayState | null>();

    plan.affectedSketchIds.forEach((sketchId) => {
        // 草图显隐是 feature 删除的派生显示语义，需要跟随 Undo/Redo 一起恢复。
        displayStates.set(sketchId, currentViewer.getSketchGroupDisplayState(sketchId));
    });

    return displayStates;
}

/**
 * 特征删除时随子特征一起删除的草图组运行时快照记录。
 */
interface FeatureDeletionSketchRuntimePatchRecord {
    /** 草图组 ID。 */
    readonly sketchId: string;
    /** 删除前草图组快照。 */
    readonly before: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>;
    /** 删除后缺失快照。 */
    readonly after: HistoryEntitySnapshot<SketchGroupRuntimeHistorySnapshot>;
}

/**
 * 捕获随特征删除的草图组运行时快照。
 *
 * @param plan 特征删除计划。
 * @param currentViewer 当前 Viewer。
 * @returns 草图组运行时快照记录集合。
 */
function captureFeatureDeletionSketchRuntimePatchRecords(
    plan: FeatureDeletionPlan,
    currentViewer: Viewer,
): FeatureDeletionSketchRuntimePatchRecord[] {
    const records: FeatureDeletionSketchRuntimePatchRecord[] = [];

    try {
        plan.deletedSketchIds.forEach((sketchId) => {
            const snapshot = currentViewer.captureSketchGroupRuntimeHistorySnapshot(
                sketchId,
                '删除特征关联草图',
            );
            if (!snapshot) {
                throw new Error(`删除特征关联草图 ${sketchId} 前无法捕获运行时快照。`);
            }

            records.push({
                sketchId,
                before: createExistingHistorySnapshot(snapshot),
                after: createMissingHistorySnapshot(),
            });
        });
    } catch (error) {
        releaseFeatureDeletionSketchRuntimePatchRecords(currentViewer, records);
        throw error;
    }

    return records;
}

/**
 * 释放未进入历史栈的草图组运行时快照。
 *
 * @param currentViewer 当前 Viewer。
 * @param records 草图组运行时快照记录集合。
 */
function releaseFeatureDeletionSketchRuntimePatchRecords(
    currentViewer: Viewer,
    records: readonly FeatureDeletionSketchRuntimePatchRecord[],
): void {
    records.forEach((record) => {
        [record.before, record.after].forEach((snapshot) => {
            if (!snapshot.exists || !snapshot.data) {
                return;
            }
            currentViewer.releaseSketchGroupRuntimeHistorySnapshot(snapshot.data);
        });
    });
}

/**
 * 恢复特征删除事务失败前被移除的草图组。
 *
 * @param currentViewer 当前 Viewer。
 * @param records 草图组运行时快照记录集合。
 */
function restoreFeatureDeletionSketchRuntimeBeforeSnapshots(
    currentViewer: Viewer,
    records: readonly FeatureDeletionSketchRuntimePatchRecord[],
): void {
    records.forEach((record) => {
        if (!record.before.exists || !record.before.data) {
            return;
        }
        currentViewer.restoreSketchGroupRuntimeHistorySnapshot(record.before.data);
    });
}

/**
 * 解析指定特征删除影响到的渲染对象。
 *
 * @param summary Worker 几何状态摘要。
 * @param featureId 特征 ID。
 * @returns 渲染对象 ID 列表。
 */
function resolveDeletedFeatureRenderObjectIds(
    summary: FeatureDeletionExecutionResult['beforeSummary'],
    featureId: string,
): string[] {
    const objectIds = new Set<string>();

    summary.objects.forEach((object) => {
        const metadata = isRecord(object.metadata) ? object.metadata : {};
        if (object.id === featureId || object.commandId === featureId || metadata.featureId === featureId) {
            objectIds.add(object.id);
        }
    });

    return Array.from(objectIds);
}

/**
 * 解析整次特征删除影响到的渲染对象。
 *
 * @param result 特征删除执行结果。
 * @returns 去重后的渲染对象 ID 列表。
 */
function resolveFeatureDeletionRenderObjectIds(result: FeatureDeletionExecutionResult): string[] {
    const objectIds = new Set<string>();

    result.plan.deletedFeatureIds.forEach((featureId) => {
        resolveDeletedFeatureRenderObjectIds(result.beforeSummary, featureId).forEach((objectId) => {
            objectIds.add(objectId);
        });
    });

    return Array.from(objectIds);
}

/**
 * 创建特征删除历史补丁。
 *
 * @param result 特征删除执行结果。
 * @param beforeSketchDisplayStates 删除前草图显示态。
 * @param afterSketchDisplayStates 删除后草图显示态。
 * @returns Feature 定义删除补丁集合。
 */
function createFeatureDeletionHistoryPatches(
    result: FeatureDeletionExecutionResult,
    beforeSketchDisplayStates: ReadonlyMap<string, SketchDisplayState | null>,
    afterSketchDisplayStates: ReadonlyMap<string, SketchDisplayState | null>,
): ReturnType<typeof createFeatureDefinitionHistoryPatch>[] {
    return result.plan.deletedFeatureIds.flatMap((featureId) => {
        const before = captureFeatureHistorySnapshotFromSummary(result.beforeSummary, featureId);
        if (!before.exists) {
            throw new Error(`特征 ${featureId} 删除成功，但无法从删除前 Worker 历史捕获 before 快照。`);
        }

        const after = createMissingHistorySnapshot();
        const operation = resolveEntityPatchOperation(before, after);

        if (operation === 'noop') {
            return [];
        }

        const featureSummary = resolveFeatureDeletionSummary(result.plan, featureId);
        const sourceSketchId = featureSummary?.sourceSketchId;

        return [createFeatureDefinitionHistoryPatch({
            entityId: featureId,
            operation,
            before,
            after,
            metadata: {
                featureId,
                sourceSketchId: sourceSketchId ?? undefined,
                sourceSketchDisplayStateBefore: sourceSketchId
                    ? beforeSketchDisplayStates.get(sourceSketchId) ?? null
                    : null,
                sourceSketchDisplayStateAfter: sourceSketchId
                    ? afterSketchDisplayStates.get(sourceSketchId) ?? null
                    : null,
                renderObjectIds: resolveDeletedFeatureRenderObjectIds(result.beforeSummary, featureId),
            },
        })];
    });
}

/**
 * 创建特征删除关联草图组运行时历史补丁。
 *
 * @param records 草图组运行时快照记录集合。
 * @returns 草图组运行时删除补丁集合。
 */
function createFeatureDeletionSketchRuntimeHistoryPatches(
    records: readonly FeatureDeletionSketchRuntimePatchRecord[],
): ReturnType<typeof createSketchGroupRuntimeHistoryPatch>[] {
    return records.map((record) => createSketchGroupRuntimeHistoryPatch({
        entityId: record.sketchId,
        operation: resolveEntityPatchOperation(record.before, record.after),
        before: record.before,
        after: record.after,
        metadata: {
            sketchId: record.sketchId,
        },
    })).filter((patch) => patch.operation !== 'noop');
}

/**
 * 提交设计树特征删除历史事务。
 *
 * @param dependencies 处理器依赖。
 * @param result 特征删除执行结果。
 * @param beforeSketchDisplayStates 删除前草图显示态。
 */
async function commitFeatureDeletionHistory(
    dependencies: FeatureTreeFeatureDeleteActionHandlerDependencies,
    result: FeatureDeletionExecutionResult,
    beforeSketchDisplayStates: ReadonlyMap<string, SketchDisplayState | null>,
    sketchRuntimePatchRecords: readonly FeatureDeletionSketchRuntimePatchRecord[],
): Promise<void> {
    const currentViewer = dependencies.getViewer();
    if (!currentViewer) {
        throw new Error('Viewer 未初始化，无法提交特征删除历史。');
    }

    const afterSketchDisplayStates = captureFeatureDeletionSketchDisplayStates(result.plan, currentViewer);
    const featurePatches = createFeatureDeletionHistoryPatches(
        result,
        beforeSketchDisplayStates,
        afterSketchDisplayStates,
    );
    const sketchRuntimePatches = createFeatureDeletionSketchRuntimeHistoryPatches(sketchRuntimePatchRecords);
    const patches = [
        ...featurePatches,
        ...sketchRuntimePatches,
    ];

    if (patches.length === 0) {
        throw new Error('特征删除已执行，但没有生成可提交的历史补丁。');
    }

    const historySeq = dependencies.allocateHistorySequence();
    const renderObjectIds = resolveFeatureDeletionRenderObjectIds(result);
    const transaction: CadTransaction = {
        id: `txn:feature-delete:${result.plan.targetFeature.featureId}:${Date.now()}`,
        documentId: dependencies.getDocumentId(),
        seq: historySeq,
        historySeq,
        label: result.plan.childFeatures.length > 0
            ? `删除 ${result.plan.targetFeature.displayName} 及相关特征`
            : `删除 ${result.plan.targetFeature.displayName}`,
        commandType: 'feature-tree.feature.delete',
        createdAt: Date.now(),
        clientId: 'web-client',
        snapshotScope: {
            ...createEmptySnapshotScope(),
            entityIds: [...result.plan.deletedFeatureIds, ...result.plan.affectedSketchIds],
            includeWorkerCommandHistory: true,
        },
        dirtyScope: {
            ...createEmptyDirtyScope(),
            featureIds: [...result.plan.deletedFeatureIds],
            sketchIds: [...result.plan.affectedSketchIds],
            renderObjectIds,
            fullRebuild: true,
        },
        changeSet: createChangeSet(patches),
        status: 'committed',
    };

    const committedTransaction = await dependencies.commitTransaction(transaction);
    if (!committedTransaction) {
        throw new Error('特征删除已执行，但历史事务未成功提交。');
    }

    dependencies.markFeatureDefinitionsDeleted(result.plan.deletedFeatureIds);
}

/**
 * 回滚已执行但历史提交失败的特征删除。
 *
 * @param currentViewer 当前 Viewer。
 * @param result 特征删除执行结果。
 * @param beforeSketchDisplayStates 删除前草图显示态。
 */
async function rollbackFeatureDeletionAfterHistoryFailure(
    currentViewer: Viewer,
    result: FeatureDeletionExecutionResult,
    beforeSketchDisplayStates: ReadonlyMap<string, SketchDisplayState | null>,
    beforeRollbackSnapshot: FeatureRollbackHistorySnapshot | null,
    sketchRuntimePatchRecords: readonly FeatureDeletionSketchRuntimePatchRecord[],
): Promise<void> {
    restoreFeatureDeletionSketchRuntimeBeforeSnapshots(currentViewer, sketchRuntimePatchRecords);
    if (beforeRollbackSnapshot && currentViewer.restoreFeatureRollbackHistorySnapshot) {
        await currentViewer.restoreFeatureRollbackHistorySnapshot(beforeRollbackSnapshot);
    } else {
        await currentViewer.replayFeatureHistoryCommands(result.beforeSummary.history, {
            rollbackCommands: result.afterSummary.history,
            beforeSummary: result.beforeSummary,
        });
    }
    beforeSketchDisplayStates.forEach((displayState, sketchId) => {
        if (!displayState) {
            return;
        }
        currentViewer.setSketchGroupDisplayState(sketchId, displayState);
    });
}

/**
 * 执行设计树特征删除并提交历史。
 *
 * @param dependencies 处理器依赖。
 * @param input 设计树特征删除动作。
 * @param confirmedPlan 用户确认过的删除计划。
 * @returns 删除是否成功。
 */
async function applyFeatureTreeFeatureDeleteActionInQueue(
    dependencies: FeatureTreeFeatureDeleteActionHandlerDependencies,
    input: FeatureTreeFeatureDeleteActionInput,
    confirmedPlan: FeatureDeletionPlan,
): Promise<boolean> {
    const currentViewer = dependencies.getViewer();
    if (!currentViewer) {
        return false;
    }

    const beforeSketchDisplayStates = captureFeatureDeletionSketchDisplayStates(confirmedPlan, currentViewer);
    const sketchRuntimePatchRecords = captureFeatureDeletionSketchRuntimePatchRecords(confirmedPlan, currentViewer);
    const beforeRollbackSnapshot = currentViewer.captureFeatureRollbackHistorySnapshot?.(
        dependencies.getDocumentId(),
    ) ?? null;
    let result: FeatureDeletionExecutionResult | null = null;
    try {
        result = await currentViewer.deleteFeatureById(input.featureId, {
            objectNameOverrides: input.objectNameOverrides,
            confirmedPlan,
        });
    } catch (error) {
        releaseFeatureDeletionSketchRuntimePatchRecords(currentViewer, sketchRuntimePatchRecords);
        throw error;
    }
    if (!result) {
        releaseFeatureDeletionSketchRuntimePatchRecords(currentViewer, sketchRuntimePatchRecords);
        return false;
    }

    try {
        await commitFeatureDeletionHistory(
            dependencies,
            result,
            beforeSketchDisplayStates,
            sketchRuntimePatchRecords,
        );
    } catch (error) {
        await rollbackFeatureDeletionAfterHistoryFailure(
            currentViewer,
            result,
            beforeSketchDisplayStates,
            beforeRollbackSnapshot,
            sketchRuntimePatchRecords,
        );
        releaseFeatureDeletionSketchRuntimePatchRecords(currentViewer, sketchRuntimePatchRecords);
        throw error;
    }

    return true;
}

/**
 * 创建设计树 Feature 删除动作处理器。
 *
 * @param dependencies 处理器依赖。
 * @returns 设计树 Feature 删除动作处理器。
 */
export function createFeatureTreeFeatureDeleteActionHandler(
    dependencies: FeatureTreeFeatureDeleteActionHandlerDependencies,
): (input: FeatureTreeFeatureDeleteActionInput) => Promise<boolean> {
    return async (input) => {
        let succeeded = false;

        try {
            const currentViewer = dependencies.getViewer();
            if (!currentViewer) {
                dependencies.message.warning('Viewer 未初始化，无法删除特征');
                return false;
            }

            const plan = await currentViewer.resolveFeatureDeletionPlan(input.featureId, {
                objectNameOverrides: input.objectNameOverrides,
            });
            if (!plan) {
                dependencies.message.warning('未找到可删除的特征');
                return false;
            }
            if (!await requestFeatureTreeDeleteConfirmation({
                dialog: dependencies.dialog,
                target: {
                    id: plan.targetFeature.featureId,
                    label: plan.targetFeature.displayName,
                    kind: 'feature',
                },
                relatedItems: createFeatureDeletionRelatedItems(plan),
            })) {
                return false;
            }

            await dependencies.runExclusive(async () => {
                succeeded = await applyFeatureTreeFeatureDeleteActionInQueue(dependencies, input, plan);
            });
        } catch (error) {
            if (
                error instanceof Error
                && error.message.startsWith(FEATURE_DELETION_PLAN_CHANGED_MESSAGE)
            ) {
                dependencies.message.warning(FEATURE_DELETION_PLAN_CHANGED_MESSAGE);
                return false;
            }
            dependencies.reportFailure(error, '删除特征失败');
            return false;
        }

        if (!succeeded) {
            dependencies.message.warning('特征删除失败');
        }

        return succeeded;
    };
}
